import { Stack } from "@mui/material";
import dayjs from "dayjs";
import { useLayoutEffect } from "react";
import { ActivityListGroup } from "web_myplann/Activities/Components/ActivityListGroup";
import { useActivityListQuery } from "web_myplann/Api/Activities/useActivityListQuery";
import { useTaskListQuery } from "web_myplann/Api/Tasks/useTaskListQuery";
import { DashboardSkeleton } from "web_myplann/Dashboard/Components/DashboardSkeleton";
import type {
	ActivityListItemResponseDTO,
	TaskListItemResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { dateFormatter } from "web_myplann/Shared/Utils/formatters";
import { sortEventsByDateAndTitle } from "web_myplann/Utils/dates";

type DashboardDataSectionProps = {
	onEmptyChange?: (isEmpty: boolean) => void;
};

export const DashboardDataSection = ({
	onEmptyChange,
}: DashboardDataSectionProps) => {
	const { t } = useResource();

	const actualDate = dateFormatter(new Date(), "", DateFormat.Iso);
	const yesterdayDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");
	const thirtyDaysInFuture = dayjs().add(30, "days").format("YYYY-MM-DD");
	const fourteenDaysInPast = dayjs().subtract(14, "days").format("YYYY-MM-DD");

	const { data: inProgressEvents, isLoading: isInProgressLoading } =
		useTaskListQuery(undefined, undefined, true);

	const { data: upcomingEvents, isLoading: isUpcomingLoading } =
		useActivityListQuery(actualDate, thirtyDaysInFuture, true);

	const { data: overdueEvents, isLoading: isOverdueLoading } =
		useActivityListQuery(fourteenDaysInPast, yesterdayDate, true);

	const isLoading =
		isInProgressLoading || isUpcomingLoading || isOverdueLoading;

	const inProgressEventsSorted = sortEventsByDateAndTitle(
		(inProgressEvents?.data ?? []) as TaskListItemResponseDTO[],
		t,
	);

	const upcomingEventsSorted = sortEventsByDateAndTitle(
		(upcomingEvents?.data ?? []) as ActivityListItemResponseDTO[],
		t,
	);

	const overdueEventsSorted = sortEventsByDateAndTitle(
		(overdueEvents?.data ?? []) as ActivityListItemResponseDTO[],
		t,
		"desc",
	);

	const hasAnyData =
		inProgressEventsSorted.length > 0 ||
		upcomingEventsSorted.length > 0 ||
		overdueEventsSorted.length > 0;

	useLayoutEffect(() => {
		onEmptyChange?.(!isLoading && !hasAnyData);
	}, [onEmptyChange, isLoading, hasAnyData]);

	if (isLoading) {
		return (
			<Stack px={1.6} pt={2.4} gap={1}>
				<DashboardSkeleton height={150} />
			</Stack>
		);
	}

	if (!hasAnyData) {
		return null;
	}

	return (
		<Stack px={1.6} gap={1} pt={1.6}>
			<ActivityListGroup
				label={t(Resources.Common.solvedEvents)}
				data={inProgressEventsSorted}
				defaultNormalSize={1}
				noDivider={
					upcomingEventsSorted?.length === 0 ||
					overdueEventsSorted?.length === 0
				}
			/>

			<ActivityListGroup
				label={t(Resources.Common.upcomingEvents)}
				data={upcomingEventsSorted}
				defaultNormalSize={1}
				noDivider={overdueEventsSorted.length === 0}
			/>

			<ActivityListGroup
				label={t(Resources.Common.overdueEvents)}
				data={overdueEventsSorted}
				defaultNormalSize={1}
				noDivider
			/>
		</Stack>
	);
};
